import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import { FixedSizeList } from "react-window";
import { RendererProps } from "../FilePreviewModal";
import VirtualizedTable from "./VirtualizedTable";
import SearchBar from "../SearchBar";
import { escapeRegex } from "../searchUtils";

interface SheetData {
  name: string;
  rows: string[][];
}

interface MatchPosition {
  row: number;
  col: number;
}

const colLabel = (idx: number) => {
  let n = idx;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

const XlsxRenderer: React.FC<RendererProps> = ({ blob }) => {
  const [sheets, setSheets] = useState<SheetData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef<FixedSizeList | null>(null);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        const arrayBuffer = await fileBlob.arrayBuffer();
        if (cancelled) return;
        const xlsxModule = await import("xlsx");
        if (cancelled) return;
        const wb = xlsxModule.read(arrayBuffer, { type: "array" });
        const result: SheetData[] = wb.SheetNames.map((name) => {
          const ws = wb.Sheets[name];
          const aoa = xlsxModule.utils.sheet_to_json<string[]>(ws, {
            header: 1,
            defval: "",
            raw: false,
          }) as string[][];
          const normalized = aoa.map((row) =>
            (row || []).map((c) => (c == null ? "" : String(c))),
          );
          return { name, rows: normalized };
        });
        if (cancelled) return;
        setSheets(result);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to render spreadsheet",
          );
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const activeSheet = useMemo(
    () => (sheets ? sheets[Math.min(activeTab, sheets.length - 1)] : null),
    [sheets, activeTab],
  );

  const maxCols = useMemo(() => {
    if (!activeSheet) return 0;
    return activeSheet.rows.reduce((m, r) => Math.max(m, r.length), 0);
  }, [activeSheet]);

  const headerCells = useMemo(
    () => Array.from({ length: maxCols }).map((_, i) => colLabel(i)),
    [maxCols],
  );

  const matchPositions = useMemo<MatchPosition[]>(() => {
    if (!activeSheet || !search) return [];
    const re = new RegExp(escapeRegex(search), "gi");
    const positions: MatchPosition[] = [];
    activeSheet.rows.forEach((row, r) => {
      row.forEach((cell, c) => {
        if (cell == null) return;
        re.lastIndex = 0;
        if (re.test(String(cell))) {
          positions.push({ row: r, col: c });
        }
      });
    });
    return positions;
  }, [activeSheet, search]);

  useEffect(() => {
    setActiveIdx(0);
  }, [matchPositions, activeTab]);

  const activeCell = matchPositions[activeIdx] ?? null;

  useEffect(() => {
    if (activeCell && listRef.current) {
      listRef.current.scrollToItem(activeCell.row, "center");
    }
  }, [activeCell]);

  const gotoNext = useCallback(() => {
    if (matchPositions.length === 0) return;
    setActiveIdx((i) => (i + 1) % matchPositions.length);
  }, [matchPositions.length]);

  const gotoPrev = useCallback(() => {
    if (matchPositions.length === 0) return;
    setActiveIdx(
      (i) => (i - 1 + matchPositions.length) % matchPositions.length,
    );
  }, [matchPositions.length]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!sheets) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (sheets.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No sheets found in the workbook.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              textTransform: "none",
            },
          }}
        >
          {sheets.map((s, i) => (
            <Tab key={`${s.name}-${i}`} label={s.name || `Sheet ${i + 1}`} />
          ))}
        </Tabs>
        <SearchBar
          value={search}
          onChange={setSearch}
          matchCount={matchPositions.length}
          currentMatch={
            matchPositions.length > 0 ? activeIdx + 1 : 0
          }
          onPrev={gotoPrev}
          onNext={gotoNext}
        />
      </Box>
      {activeSheet &&
        (activeSheet.rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">This sheet is empty.</Alert>
          </Box>
        ) : (
          <VirtualizedTable
            rows={activeSheet.rows}
            headerCells={headerCells}
            searchQuery={search}
            activeCell={activeCell}
            listRef={listRef}
          />
        ))}
    </Box>
  );
};

export default XlsxRenderer;
